import React, { useState, useEffect } from 'react';
import { Button, Table, Space, Modal, Tag, message, Alert, Empty, Row, Col, Input } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, UserOutlined, RobotOutlined } from '@ant-design/icons';
import apiClient from '../api';

const UserMappingManager = ({ templates, loading, onUpdate }) => {
  const [mappings, setMappings] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    userPhoneNumber: '',
    templateId: '',
    priority: 0
  });

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      const response = await apiClient.get('/user-mappings');
      if (response.data.success) {
        setMappings(response.data.mappings || []);
      }
    } catch (error) {
      console.error('Error loading user mappings:', error);
    }
  };

  const handleCreate = () => {
    setEditingMapping(null);
    setFormData({ userPhoneNumber: '', templateId: '', priority: 0 });
    setIsModalVisible(true);
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      userPhoneNumber: mapping.userPhoneNumber,
      templateId: mapping.templateId,
      priority: mapping.priority || 0
    });
    setIsModalVisible(true);
  };

  // Open Ant Design modal to confirm delete
  const handleDelete = (mapping) => {
    if (!mapping) return;
    console.log('[UserMappingManager] Delete clicked for mapping:', mapping);
    setDeleteTarget(mapping);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const phone = deleteTarget.userPhoneNumber;
    if (!phone) return;

    try {
      const response = await apiClient.delete(
        `/user-mappings/${encodeURIComponent(phone)}`
      );
      if (response.data.success) {
        message.success('User mapping deleted successfully');
        setDeleteTarget(null);
        loadMappings();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Failed to delete mapping');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete mapping');
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.userPhoneNumber || !formData.templateId) {
      message.error('Please fill in all required fields');
      return;
    }

    try {
      const url = editingMapping 
        ? `/user-mappings/${encodeURIComponent(editingMapping.userPhoneNumber)}`
        : '/user-mappings';
      
      const method = editingMapping ? 'put' : 'post';
      const response = await apiClient[method](url, formData);

      if (response.data.success) {
        message.success(`User mapping ${editingMapping ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        loadMappings();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Failed to save mapping');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save mapping');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'User Phone Number',
      dataIndex: 'userPhoneNumber',
      key: 'userPhoneNumber',
      width: 250,
      render: (text) => (
        <Space>
          <UserOutlined style={{ color: '#667eea' }} />
          <Tag color="blue" style={{ fontSize: 14 }}>{text}</Tag>
        </Space>
      ),
    },
    {
      title: 'Assigned Agent',
      dataIndex: 'templateId',
      key: 'templateId',
      render: (templateId) => {
        const template = templates.find(t => t.templateId === templateId);
        return (
          <Space direction="vertical" size={2}>
            <Tag color="green" icon={<RobotOutlined />}>
              {templateId}
            </Tag>
            {template && (
              <span style={{ fontSize: 12, color: '#666' }}>
                {template.agentName}
              </span>
            )}
            {!template && (
              <Tag color="red">Template not found</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority) => (
        <Tag color={priority > 0 ? 'orange' : 'default'}>{priority}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <h2 style={{ margin: 0 }}>👤 User-to-Agent Mappings</h2>
            <p style={{ color: '#666', margin: '4px 0 0 0' }}>
              Assign specific agents to individual users (master agent system)
            </p>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              size="large"
              onClick={handleCreate}
              disabled={templates.length === 0}
            >
              Map User
            </Button>
          </Col>
        </Row>
      </div>

      <Alert
        message="How it works"
        description={
          <div>
            <p style={{ marginBottom: 8 }}>
              <strong>Map individual users to specific agents</strong>
            </p>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>User +1-234-5678 → Travel Agent</li>
              <li>User +1-987-6543 → Support Agent</li>
            </ul>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              💡 All messages go to one master bot number, but different users get different agents
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Delete confirmation modal */}
      <Modal
        title="Delete User Mapping"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleConfirmDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <p>Are you sure you want to remove this user mapping? This action cannot be undone.</p>
        {deleteTarget && (
          <p>
            <strong>User Phone Number:</strong>{' '}
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {deleteTarget.userPhoneNumber}
            </Tag>
          </p>
        )}
      </Modal>

      {templates.length === 0 && (
        <Alert
          message="No Templates Available"
          description="You need to create at least one agent template before creating mappings."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table 
        columns={columns} 
        dataSource={mappings}
        rowKey="userPhoneNumber"
        loading={loading}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: (
            <Empty 
              description="No user mappings yet. Map users to agent templates!"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />

      <Modal
        title={editingMapping ? "✏️ Edit User Mapping" : "➕ Map User to Agent"}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              User Phone Number <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              placeholder="e.g., 1111111111@c.us"
              value={formData.userPhoneNumber}
              onChange={(e) => setFormData({ ...formData, userPhoneNumber: e.target.value })}
              disabled={!!editingMapping}
            />
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Format: phone number with @c.us suffix (e.g., 1111111111@c.us)
            </p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Agent Template <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              style={{ width: '100%', padding: '8px', fontSize: 14, borderRadius: 4, border: '1px solid #d9d9d9' }}
              value={formData.templateId}
              onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
            >
              <option value="">Select an agent template</option>
              {templates.map(template => (
                <option key={template.templateId} value={template.templateId}>
                  {template.templateId} - {template.agentName}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Priority
            </label>
            <Input
              type="number"
              placeholder="0"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
            />
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Higher priority = checked first (default: 0)
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserMappingManager;

